import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatTime } from '@/lib/utils';
import { CheckCircle2 } from 'lucide-react';
import { saveAnnouncement, readAnnouncements } from '@/lib/announcements';
import { portalText } from '@/i18n/carrierPortal';

const OPEN_STATES = ['no-info', 'confirmed', 'en-route', 'rescheduled', 'no-power'];

const Field = ({ label, className, children }) => (
  <label className={`space-y-1 ${className || ''}`}>
    <span className="block text-[11px] font-medium text-muted-foreground">{label}</span>
    {children}
  </label>
);

// One row per open loading of the carrier: plate, driver, arrival, or "no truck".
const LoadingRow = ({ loading, txt, onSaved }) => {
  const saved = readAnnouncements()[loading.orderId];
  const [plate, setPlate] = useState(saved?.plate || (loading.plate !== '—' ? loading.plate : ''));
  const [driver, setDriver] = useState(saved?.driver || (loading.driver.name !== '—' ? loading.driver.name : ''));
  const [eta, setEta] = useState(saved?.eta || formatTime(loading.plannedAt));
  const [state, setState] = useState(saved ? (saved.noTruck ? 'noTruck' : 'saved') : null);

  const save = (noTruck) => {
    saveAnnouncement(loading.orderId, noTruck ? { noTruck: true } : { plate, driver, eta });
    setState(noTruck ? 'noTruck' : 'saved');
    onSaved?.();
  };

  return (
    <div className="rounded-xl border bg-white p-3 space-y-2" data-testid={`announce-${loading.orderId}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-sm font-semibold">{loading.orderId}</div>
        <div className="text-xs text-muted-foreground">{txt.planned} {formatTime(loading.plannedAt)}</div>
      </div>
      <div className="text-xs text-muted-foreground">{loading.material} · {loading.weight} t</div>
      {/* Plate and arrival side by side, the driver's full name gets its own row. */}
      <div className="grid grid-cols-2 gap-2">
        <Field label={txt.plate}>
          <Input aria-label={txt.plate} value={plate} onChange={e => setPlate(e.target.value)} className="h-9 text-sm" />
        </Field>
        <Field label={txt.eta}>
          <Input aria-label={txt.eta} type="time" value={eta} onChange={e => setEta(e.target.value)} className="h-9 text-sm" />
        </Field>
        <Field label={txt.driver} className="col-span-2">
          <Input aria-label={txt.driver} value={driver} onChange={e => setDriver(e.target.value)} className="h-9 text-sm" />
        </Field>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" className="h-8" disabled={!plate || !eta} onClick={() => save(false)}>{txt.save}</Button>
        <Button size="sm" variant="ghost" className="h-8 text-red-700" onClick={() => save(true)}>{txt.noTruck}</Button>
        {state && (
          <span className="ml-auto flex items-center gap-1 text-xs text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> {state === 'noTruck' ? txt.noTruckSaved : txt.saved}
          </span>
        )}
      </div>
    </div>
  );
};

// The page a carrier's dispatcher opens from a link: their open loadings for today.
export const CarrierAnnounce = ({ carrier, loadings, onSaved }) => {
  const txt = portalText(carrier.lang);
  const open = loadings.filter(l => l.carrierId === carrier.id && OPEN_STATES.includes(l.state))
    .sort((a, b) => a.plannedAt.localeCompare(b.plannedAt));

  return (
    <div className="space-y-3">
      <div>
        <div className="text-[11px] font-bold tracking-wider text-primary">OMYA · POMEZÍ</div>
        <div className="text-lg font-bold">{txt.title}</div>
        <div className="text-sm font-medium">{carrier.name}</div>
        <div className="text-xs text-muted-foreground mt-1">{txt.intro}</div>
        <div className="text-[10px] text-amber-700 mt-1"><span aria-hidden="true">●</span> {txt.demo}</div>
      </div>
      {open.length === 0 && <div className="text-sm text-muted-foreground">{txt.empty}</div>}
      {open.map(l => <LoadingRow key={l.id} loading={l} txt={txt} onSaved={onSaved} />)}
    </div>
  );
};
